import React, { useCallback, useState } from "react";
import Select from "react-select";

import { useTheme } from "../context/ThemeContext";

const formatOptionLabel = ({ label }) => (
  <div className="flex" dangerouslySetInnerHTML={{ __html: label }} />
);

const PlaceholderFC = ({ prefix, body }) => (
  <button className={`flex justify-between items-center grow`}>
    <div className="text-left items-center">
      <div
        className="h8 opacity-60"
        dangerouslySetInnerHTML={{ __html: prefix }}
      />
      <h6
        className="font-bold text-black-lighter"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  </button>
);

const FilterBox = ({
  filterLabel,
  options,
  filterCallback,
  type = "default"
}) => {
  const [values, setValues] = useState({
    programType: "",
    location: "",
    programLocation: ""
  });

  const { colorMode } = useTheme();

  const customStyles = {
    menu: (provided) => ({
      //listbox
      ...provided,
      borderRadius: 0,
      padding: 0,
      margin: 0,
      background: colorMode === "light" ? provided.background : "transparent",
      border: "1px solid rgba(231, 231, 242, 0.3)",
      boxShadow: 0
    }),
    menuList: (provided) => ({
      ...provided,
      background: colorMode === "light" ? "white" : "transparent",
      padding: 0
    }),
    option: (provided, state) => ({
      ...provided,
      zIndex: 20,
      color: colorMode === "light" ? "#4D5A62" : "white",
      background: colorMode === "light" ? "white" : "transparent",
      fontWeight: state.isSelected ? 700 : 400,
      height: "100%",
      display: state.isSelected ? "none" : "block"
    }),
    control: (provided) => ({
      ...provided,
      background: colorMode === "light" ? "white" : "#1b1b1b",
      padding: "4px",
      height: "100%",
      border: 0,
      borderRadius: 0
    }),
    singleValue: (provided) => {
      const transition = "opacity 300ms";
      const color = colorMode === "light" ? "#4D5A62" : "white";
      const fontSize = "16px";
      const fontWeight = "bold";

      return {
        ...provided,
        transition,
        color,
        fontSize,
        fontWeight
      };
    },
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: "#ffffff"
      };
    },
    dropdownIndicator: (base) => ({
      ...base,
      color: colorMode === "light" ? "#4D5A62" : "white",
      paddingLeft: 0,
      paddingRight: 0
    }),
    indicatorSeparator: () => null
  };

  const handleClear = useCallback(() => {
    const clearedValues = {
      programType: "",
      location: "",
      programLocation: ""
    };
    setValues(clearedValues);
    filterCallback("clear");
  }, [filterCallback]);

  const handleChange = useCallback(
    (prop) => (value) => {
      const updatedValues = { ...values, [prop]: value ?? "" };

      // activate handleClear if object empty
      const isHandleClear = Object.values(updatedValues).every((item) => !item);

      if (isHandleClear) {
        setValues(updatedValues);
        filterCallback("clear");
      } else {
        setValues(updatedValues);
        filterCallback({ ...updatedValues, action: "change" });
      }
    },
    [filterCallback, values]
  );

  return (
    <div
      className={`py-5 sm:px-12 z-10 bg-transparent sm:bg-white sm:dark:bg-gray-darkest sm:dark:border-0.5 sm:dark:border-gray-testimonial 
    ${
      type === "no-back"
        ? "!relative !border-0 !bg-transparent !p-0 !backdrop-blur-none"
        : ""
    }`}
    >
      <h3
        className={`text-white sm:text-black-lighter font-bold sm:font-normal font-body sm:font-alice pb-5 text-center sm:text-left
      ${type === "no-back" ? "!pb-36" : ""}`}
      >
        {filterLabel}
      </h3>
      <div
        className={`flex flex-col lg:flex-row gap-6
      ${
        type === "no-back"
          ? "!absolute !flex-col-reverse !w-full !top-[9px] !gap-4"
          : ""
      }`}
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 w-full sm:border border-black-lighter/30 dark:border-gray-testimonial/30 sm:divide-x dark:divide-gray-testimonial/30">
          {options.map((option, index) => {
            return (
              <div
                key={index}
                className={`grow shrink basis-0 h-full
                ${type === "no-back" ? "!h-14" : ""}`}
                style={{ zIndex: 10 * (options.length - index) }}
              >
                <Select
                  formatOptionLabel={formatOptionLabel}
                  options={option.list}
                  className="h-full bg-white  dark:border dark:border-gray-testimonial/30 dark:sm:border-0 dark:sm:bg-transparent rounded-none"
                  styles={customStyles}
                  onChange={handleChange(option.name)}
                  value={values[option.name] || option.defaultValue || null}
                  isClearable
                  removeSelected={true}
                  maxMenuHeight={170}
                  placeholder={
                    <PlaceholderFC
                      prefix={option.label.prefix}
                      body={option.label.body}
                    />
                  }
                />
              </div>
            );
          })}
        </div>
        <button
          className={`shrink-0 hidden sm:block p-4 border rounded-full dark:rounded-sm dark:border-white border-black-lighter font-bold text-black-lighter dark:text-white
          ${
            type === "no-back"
              ? "!ml-auto !border-none !p-0 !text-xs !text-right !underline"
              : ""
          }`}
          onClick={handleClear}
        >
          Clear selection
        </button>
      </div>
    </div>
  );
};

export default FilterBox;
